import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { useHistory } from 'react-router-dom';

import './stylesheet.css';

import * as orgActions from '../../../../actions/org';

import { fileBacklinks, fileForwardLinks, nodesInFile } from '../../../../lib/org_roam';
import { loadDatabase } from '../../../../lib/org_roam_db';
import { filePathToUrl } from '../../../../lib/dashboard';

// One component for both directions. They differ only in which query they run
// and what the copy says: the layout - grouped by node, each entry a link plus
// the sentence it sits in - is the same thing read two ways, and two components
// would be one copy-paste twin drifting away from the other.
const DIRECTIONS = {
  in: {
    title: 'Back-links',
    query: fileBacklinks,
    entries: (group) => group.backlinks,
    // For an inbound link, the interesting end is where it came from.
    other: (link) => link.sourceNode,
    unknownLabel: (link) => link.file,
    unknownTitle: 'This link sits above the first node in its file, so there is nothing to open.',
    countNoun: (n) => `${n === 1 ? 'link' : 'links'} in`,
    nothing: 'Nothing links to',
  },
  out: {
    title: 'Forward-links',
    query: fileForwardLinks,
    entries: (group) => group.links,
    other: (link) => link.destNode,
    // A destination the index does not have: the note is gone, was never
    // indexed, or the id is a typo. Showing the raw id is the only useful thing
    // left, and it is exactly what the reader needs in order to go and fix it.
    unknownLabel: (link) => link.dest,
    unknownTitle: 'No node with this id is in the index — the note may be gone, or never synced.',
    countNoun: (n) => `${n === 1 ? 'link' : 'links'} out`,
    nothing: 'Nothing is linked from',
  },
};

const RoamLinksModal = ({ direction, path, org, onClose }) => {
  const history = useHistory();
  const spec = DIRECTIONS[direction] || DIRECTIONS.in;

  // Read once per opening rather than on every render: the index only changes on
  // an explicit sync, and re-reading localStorage while the modal is open would
  // parse the whole thing for nothing.
  const database = useMemo(() => loadDatabase(), []);

  const nodes = useMemo(() => nodesInFile(database, path), [database, path]);
  const groups = useMemo(() => spec.query(database, path), [spec, database, path]);
  const total = groups.reduce((count, group) => count + spec.entries(group).length, 0);

  // Close first, then navigate: the popup belongs to the file being left, and
  // the parked target is read by whichever file finishes loading next - which is
  // this same file when the link points inside it, so no push is needed then.
  const openNode = (node) => {
    if (!node) return;
    org.setPendingRoamTarget(node.id, node.file);
    onClose();
    if (node.file !== path) history.push(filePathToUrl(node.file));
  };

  const openRoamPage = () => {
    onClose();
    history.push('/roam');
  };

  const renderEmpty = () => {
    if (!database.nodes.length) {
      return (
        <div className="roam-links__empty">
          There is no org-roam index in this browser yet, so nothing is known about what links
          where.
          <button className="roam-links__link-button" onClick={openRoamPage}>
            Build one on the org-roam page
          </button>
        </div>
      );
    }

    // Outbound links do not need this file to hold a node - text above the first
    // headline can link out perfectly well - so the "no :ID: here" explanation
    // only applies to the inbound direction.
    if (direction === 'in' && !nodes.length) {
      return (
        <div className="roam-links__empty">
          No node in this file carries an <code>:ID:</code> property, so nothing can link to it. If
          you have just added one, the index needs another sync.
          <button className="roam-links__link-button" onClick={openRoamPage}>
            Sync the index
          </button>
        </div>
      );
    }

    return (
      <div className="roam-links__empty">
        {spec.nothing} this file yet — at least not as of the last sync.
      </div>
    );
  };

  const groupLabel = (node) => {
    if (!node) return 'Above the first node';
    return node.level === 0 ? `${node.title} (whole file)` : node.title;
  };

  return (
    <div className="roam-links">
      <h2 className="roam-links__title">{spec.title}</h2>

      <div className="roam-links__subtitle">
        <code>{path}</code>
        {!!database.syncedAt && (
          <span className="roam-links__synced">
            {' '}
            · index synced {new Date(database.syncedAt).toLocaleString()}
          </span>
        )}
      </div>

      {total === 0 ? (
        renderEmpty()
      ) : (
        <>
          <div className="roam-links__summary">
            <strong>{total}</strong> {spec.countNoun(total)}, across{' '}
            <strong>{groups.length}</strong> {groups.length === 1 ? 'node' : 'nodes'} of this file
          </div>

          <div className="roam-links__groups">
            {groups.map((group) => (
              <div className="roam-links__group" key={group.node ? group.node.id : 'preamble'}>
                <div className="roam-links__target">
                  {group.node ? (
                    <button
                      className="roam-links__target__name"
                      title="Jump to this node"
                      onClick={() => openNode(group.node)}
                    >
                      {groupLabel(group.node)}
                    </button>
                  ) : (
                    <span className="roam-links__target__name roam-links__target__name--none">
                      {groupLabel(null)}
                    </span>
                  )}
                  <span className="roam-links__target__count">{spec.entries(group).length}</span>
                </div>

                {!!group.node && group.node.olp.length > 0 && (
                  <div className="roam-links__target__olp">{group.node.olp.join(' › ')}</div>
                )}

                <ul className="roam-links__list">
                  {spec.entries(group).map((link, index) => {
                    const other = spec.other(link);

                    return (
                      <li className="roam-links__item" key={`${link.file}-${link.line}-${index}`}>
                        {other ? (
                          <button className="roam-links__source" onClick={() => openNode(other)}>
                            {other.title}
                          </button>
                        ) : (
                          <span
                            className="roam-links__source roam-links__source--unknown"
                            title={spec.unknownTitle}
                          >
                            {spec.unknownLabel(link)}
                          </span>
                        )}
                        {!!link.context && (
                          <div className="roam-links__context">{link.context}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  path: state.org.present.get('path'),
});

const mapDispatchToProps = (dispatch) => ({
  org: bindActionCreators(orgActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoamLinksModal);
